import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  CameraOff,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Diff,
  Fingerprint,
  History,
  Octagon,
  PlayCircle,
  Plug,
  RefreshCw,
  RotateCcw,
  ServerCog,
  Settings2,
  Unplug,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { runModeLabel } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { DiffPane } from '@/components/ui/DiffPane';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { LogStreamPane } from '@/components/ui/LogStreamPane';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { RunStatusBadge } from '@/components/ui/RunStatusBadge';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { Tabs, type TabItem } from '@/components/ui/Tabs';
import { useRunDiff } from '@/hooks/useAi';
import { useCustomer } from '@/hooks/useCustomers';
import { useRoutines } from '@/hooks/useRoutines';
import {
  isActive,
  isTerminal,
  runKeys,
  useAbortRun,
  useConfirmRun,
  useRollbackRun,
  useRun,
  useRunSnapshots,
  useRunSteps,
  type RunDetail,
  type RunStep,
  type Snapshot,
} from '@/hooks/useRuns';
import { useRunStream } from '@/hooks/useRunStream';
import { useSystems } from '@/hooks/useSystems';
import {
  failureBoxClasses,
  failureGroupLabel,
  failureTextClass,
  failureTone,
  rohausgabe,
  type RunFailureSummary,
  type StepFailureReason,
  type WithRunFailure,
  type WithStepFailure,
} from '@/lib/failureReason';

/** Step samt berechneter Klartext-Ursache (Backend liefert failure_reason mit). */
export type Step = WithStepFailure<RunStep>;

/**
 * /runs/$id - Run-Detail mit Live-Stream (Sprint 5, WT-506/WT-507).
 *
 * Layout:
 *  - Header mit Run-Metadaten, Status-Badge (live), Snapshot-IDs
 *  - Toolbar: Abbrechen (läuft), Rollback (mit Snapshot), Erneut (finished)
 *  - Tabs: Live | Steps | Snapshots
 *  - Mobile: Tabs stacken vertikal, LogPane dominant
 *
 * A11y: Status-Wechsel werden via aria-live="polite" am Header announced
 * (nur bei Wechsel, nicht bei jeder Log-Line - sonst Screen-Reader-Spam).
 */

export const Route = createFileRoute('/runs/$id')({
  component: RunDetailPage,
});

type DetailTab = 'live' | 'diff' | 'steps' | 'snapshots';

function RunDetailPage(): JSX.Element {
  const { id } = useParams({ from: '/runs/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const runQuery = useRun(id);
  const stepsQuery = useRunSteps(id);
  const stream = useRunStream(id);

  const [tab, setTab] = useState<DetailTab>('live');
  const [confirmAbort, setConfirmAbort] = useState(false);
  const [confirmRollback, setConfirmRollback] = useState(false);
  const [confirmEchtlauf, setConfirmEchtlauf] = useState(false);

  const abort = useAbortRun();
  const rollback = useRollbackRun();
  const confirmRun = useConfirmRun();

  // Backend hängt an Run und Step die berechnete Klartext-Ursache (siehe
  // lib/failureReason.ts) - die Hooks kennen die Felder noch nicht.
  const run = runQuery.data as WithRunFailure<RunDetail> | undefined;
  const steps = useMemo(() => (stepsQuery.data?.data ?? []) as Step[], [stepsQuery.data]);
  const isRunning = isActive(run?.status);
  const isFinished = run ? isTerminal(run.status) : false;

  // Namens-Auflösung: Zielsysteme -> Hostname, Routinen -> Name, Kunde -> Name.
  const systemsQuery = useSystems({
    ...(run?.customer_id ? { customerId: run.customer_id } : {}),
    limit: 200,
  });
  const routinesQuery = useRoutines();
  const customerQuery = useCustomer(run?.customer_id);

  const systemName = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of systemsQuery.data?.data ?? []) m.set(s.id, s.hostname);
    return m;
  }, [systemsQuery.data]);
  const hostLabel = (sysId: string | null | undefined): string => {
    if (!sysId) return 'global';
    return systemName.get(sysId) ?? sysId.slice(0, 8);
  };

  const routineName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of routinesQuery.data?.data ?? []) m.set(r.id, r.display_name);
    return m;
  }, [routinesQuery.data]);

  const customerLabel =
    customerQuery.data?.name ?? run?.customer_id.slice(0, 8) ?? '';
  const routineLabel =
    run && run.routine_ids.length > 0
      ? run.routine_ids.map((rid) => routineName.get(rid) ?? rid.slice(0, 8)).join(', ')
      : '–';
  const systemHostnames = run
    ? run.target_system_ids.map((sid) => hostLabel(sid))
    : [];

  // Snapshot-Liste (nur sinnvoll wenn finished oder zumindest gestartet).
  const snapshotsQuery = useRunSnapshots(id, { enabled: Boolean(run) });
  const hasSnapshot = (snapshotsQuery.data?.data ?? []).some((s) => s.state === 'created');

  // Diff-Daten (Sprint 6 Mount, WT-504): Backend liefert /runs/{id}/diff.
  const diffQuery = useRunDiff(id, { enabled: Boolean(run) });
  const diffSystemCount = diffQuery.data?.systems.length ?? 0;
  const hasDiff = diffSystemCount > 0;
  const isDryRunWithDiff = run?.mode === 'check' && hasDiff;
  // ADR-011 Etappe 2: Client-Runs liefern nur Rohlogs (run_steps.diff bleibt
  // NULL, /runs/{id}/diff ist leer). Der Confirm-CTA muss trotzdem erscheinen,
  // sonst gibt es keinen UI-Weg zu POST /runs/{id}/confirm. Der Server lehnt
  // Confirm ab, wenn der Dry-Run-Status nicht success|partial ist - die
  // UI-Bedingung spiegelt genau das.
  const isDryRunConfirmableWithoutDiff =
    run?.mode === 'check' &&
    (run.status === 'success' || run.status === 'partial') &&
    !hasDiff &&
    !diffQuery.isLoading;

  const tabItems: TabItem<DetailTab>[] = [
    { id: 'live', label: 'Live-Output' },
    {
      id: 'diff',
      label: 'Diff',
      trailing: hasDiff ? <Badge tone="primary">{diffSystemCount}</Badge> : null,
    },
    { id: 'steps', label: 'Steps' },
    { id: 'snapshots', label: 'Snapshots', disabled: !run },
  ];

  // Hosts aus Steps für den LogPane-Filter (bei Backend kommen Hosts nicht
  // direkt im RunStep - wir nutzen system_id als pseudo-host falls vorhanden).
  const hosts = useMemo(() => {
    const set = new Set<string>();
    for (const s of steps) {
      if (s.system_id) set.add(s.system_id.slice(0, 8));
    }
    for (const l of stream.lines) {
      if (l.host) set.add(l.host);
    }
    return Array.from(set);
  }, [steps, stream.lines]);

  // Wenn Stream einen Status meldet, invalidieren wir Run + Steps einmal.
  // WICHTIG: Nur [stream.status] als Dependency - `runQuery`/`qc` sind stabil
  // bzw. via queryClient adressiert. Früher stand `runQuery` im Dep-Array und
  // erzeugte eine Endlos-Refetch-Schleife (neues Query-Objekt pro Render).
  useEffect(() => {
    if (!stream.status) return;
    void qc.invalidateQueries({ queryKey: runKeys.detail(id) });
    void qc.invalidateQueries({ queryKey: runKeys.steps(id) });
  }, [stream.status, qc, id]);

  const onAbortConfirm = (): void => {
    setConfirmAbort(false);
    abort.mutate(id);
  };

  const onRollbackConfirm = (): void => {
    setConfirmRollback(false);
    rollback.mutate(
      { runId: id },
      {
        onSuccess: () => {
          void runQuery.refetch();
        },
      }
    );
  };

  // Dry-Run -> Echtlauf via POST /runs/{dry_run_id}/confirm. Routine + Systeme
  // werden serverseitig aus dem Dry-Run übernommen; wir navigieren danach direkt
  // auf das neu erzeugte Echtlauf-Detail (kein leerer /runs/new-Wizard mehr).
  const onEchtlaufConfirm = (): void => {
    setConfirmEchtlauf(false);
    confirmRun.mutate(
      { dryRunId: id },
      {
        onSuccess: (res) => {
          void navigate({ to: '/runs/$id', params: { id: res.run_id } });
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Link to="/runs" className="inline-flex items-center gap-1 text-sm text-muted hover:text-text">
        <ArrowLeft className="size-3" /> Zur Run-History
      </Link>

      {runQuery.isLoading && <SkeletonRows rows={3} cols={2} />}
      {runQuery.isError && <ErrorAlert error={runQuery.error} />}

      {run && (
        <>
          <header
            className="flex flex-wrap items-start justify-between gap-3"
            aria-live="polite"
            data-testid="run-detail-header"
          >
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-semibold">
                  Run <code className="font-mono text-base">{run.id.slice(0, 8)}</code>
                </h1>
                <RunStatusBadge status={run.status} data-testid="run-status-badge" />
                <Badge tone={run.mode === 'apply' ? 'warning' : 'neutral'}>
                  {runModeLabel(run.mode)}
                </Badge>
                {hasSnapshot && <Badge tone="success">Snapshot vorhanden</Badge>}
                {run.snapshot_waiver_reason && (
                  <Badge tone="warning" data-testid="run-waiver-badge">
                    Snapshot-Verzicht
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-sm text-muted">
                <RelativeTime
                  value={run.started_at ?? run.created_at}
                  variant="absolute-with-tooltip"
                />{' '}
                · Kunde <span className="text-text">{customerLabel}</span> · Routine{' '}
                <span className="text-text">{routineLabel}</span> · triggered_by{' '}
                <code className="font-mono">{run.triggered_by}</code>
              </p>
              <p className="mt-1 text-xs text-muted">
                {systemHostnames.length > 0 ? (
                  <>
                    System(e):{' '}
                    {systemHostnames.map((h, i) => (
                      <span key={`${h}-${i}`}>
                        {i > 0 && ', '}
                        <code className="font-mono">{h}</code>
                      </span>
                    ))}
                  </>
                ) : (
                  <>Keine Zielsysteme.</>
                )}
              </p>
              {stream.connection !== 'open' && stream.connection !== 'idle' && (
                <p className="mt-1 flex items-center gap-2 text-xs text-warning">
                  <span>
                    WebSocket: {stream.connection}
                    {stream.reconnectAttempts > 0 &&
                      ` (Versuch ${stream.reconnectAttempts})`}
                  </span>
                  {!isFinished &&
                    (stream.connection === 'closed' || stream.connection === 'error') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<Plug className="size-3" />}
                        onClick={() => stream.reconnect()}
                        data-testid="run-stream-reconnect"
                      >
                        Neu verbinden
                      </Button>
                    )}
                </p>
              )}
              {run.aborted_reason && (
                <p className="mt-1 text-xs text-warning">
                  Abort-Reason: <code className="font-mono">{run.aborted_reason}</code>
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<RefreshCw className="size-3" />}
                onClick={() => void runQuery.refetch()}
                loading={runQuery.isFetching}
              >
                Aktualisieren
              </Button>
              {isRunning && (
                <Button
                  variant="danger"
                  size="sm"
                  leftIcon={<Octagon className="size-3" />}
                  onClick={() => setConfirmAbort(true)}
                  loading={abort.isPending}
                  data-testid="run-abort"
                >
                  Abbrechen
                </Button>
              )}
              {isFinished && hasSnapshot && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<RotateCcw className="size-3" />}
                  onClick={() => setConfirmRollback(true)}
                  loading={rollback.isPending}
                  data-testid="run-rollback"
                >
                  Rollback
                </Button>
              )}
              {isFinished && (
                <Link to="/runs/new">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<PlayCircle className="size-3" />}
                  >
                    Neue Wartung
                  </Button>
                </Link>
              )}
            </div>
          </header>

          {abort.isError && <ErrorAlert error={abort.error} />}
          {rollback.isError && <ErrorAlert error={rollback.error} />}
          {confirmRun.isError && <ErrorAlert error={confirmRun.error} />}

          {/* Wichtigste Information des ganzen Bildschirms: WARUM ist der Lauf
              rot? Steht über allem anderen, damit niemand mehr die Rohausgabe
              lesen muss, um "Yubikey nicht beruehrt" zu erkennen.
              Ohne klassifizierte Ursache fällt ein blockierter Lauf auf den
              Kasten unten zurück - der Satz "am System wurde nichts
              verändert" darf bei KEINEM blockierten Lauf fehlen. */}
          {run.failure ? (
            <RunFailureBanner failure={run.failure} />
          ) : (
            run.status === 'blocked' && <BlockedRunBanner />
          )}

          {/* Womit wurde dieser Lauf überhaupt ausgeführt? Die Warnung des
              Clients selbst landet auf stderr in einem abgetrennten screen,
              den niemand liest - Colin sitzt hier im Browser. */}
          <ClientVersionNotice
            version={run.client_version ?? null}
            outdated={run.client_outdated ?? null}
          />

          {/* Snapshot-Verzicht (Migration 0029): dieser Lauf fuhr bewusst
              ohne Rollback-Netz. Grund, Ersatzsicherung, Zeitpunkt und
              Urheber stehen am Lauf - genau die Spur, die beim L-Quadrat-
              SQL-Direkteintrag am 20.08.2026 gefehlt hat. */}
          {run.snapshot_waiver_reason && (
            <SnapshotWaiverNotice
              reason={run.snapshot_waiver_reason}
              evidence={run.snapshot_waiver_evidence ?? null}
              at={run.snapshot_waiver_at ?? null}
              actor={run.snapshot_waiver_actor ?? null}
            />
          )}

          {(isDryRunWithDiff || isDryRunConfirmableWithoutDiff) && (
            <RealRunCta
              diffSystemCount={diffSystemCount}
              hasDiff={hasDiff}
              onShowDiff={() => setTab(hasDiff ? 'diff' : 'live')}
              onStartEchtlauf={() => setConfirmEchtlauf(true)}
              starting={confirmRun.isPending}
            />
          )}

          <Tabs items={tabItems} active={tab} onChange={setTab} ariaLabel="Run-Detail-Tabs">
            {tab === 'live' && (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[280px_1fr]">
                <StepsList steps={steps} hostLabel={hostLabel} />
                <LogStreamPane lines={stream.lines} hosts={hosts} />
              </div>
            )}
            {tab === 'diff' && (
              <DiffTabContent
                diff={diffQuery.data}
                isLoading={diffQuery.isLoading}
                error={diffQuery.error}
                isDryRun={run.mode === 'check'}
              />
            )}
            {tab === 'steps' && <StepsTable steps={steps} hostLabel={hostLabel} />}
            {tab === 'snapshots' && (
              <SnapshotsPane
                snapshots={snapshotsQuery.data?.data ?? []}
                isLoading={snapshotsQuery.isLoading}
                error={snapshotsQuery.error}
              />
            )}
          </Tabs>
        </>
      )}

      <Dialog
        open={confirmAbort}
        onClose={() => setConfirmAbort(false)}
        title="Run wirklich abbrechen?"
        description="Laufende Tasks werden mit SIGTERM beendet, Tasks in der Queue bleiben unausgeführt."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmAbort(false)}>
              Nein, weiterlaufen
            </Button>
            <Button variant="danger" onClick={onAbortConfirm} loading={abort.isPending}>
              Abbrechen bestätigen
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted">
          Das System bleibt im aktuellen Zwischenzustand. Bei Echtlauf-Run mit Snapshot
          kann anschließend Rollback ausgelöst werden.
        </p>
      </Dialog>

      <Dialog
        open={confirmRollback}
        onClose={() => setConfirmRollback(false)}
        title="Auf Pre-Snapshot zurücksetzen?"
        description="Alle Änderungen seit dem Snapshot werden verworfen."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmRollback(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={onRollbackConfirm}
              loading={rollback.isPending}
              leftIcon={<RotateCcw className="size-4" />}
            >
              Rollback bestätigen
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2 text-sm">
          <p>Konsequenzen:</p>
          <ul className="list-disc pl-5 text-muted">
            <li>VM-State wird auf Snapshot-Zeitpunkt zurückgesetzt</li>
            <li>Änderungen am System (Files, Pakete, Services) werden verworfen</li>
            <li>Daten innerhalb der VM ab Snapshot gehen verloren</li>
            <li>Es entsteht ein Audit-Eintrag <code>run.rollback</code></li>
          </ul>
          {(snapshotsQuery.data?.data ?? []).length > 0 && (
            <p className="mt-2">
              Snapshots:{' '}
              {(snapshotsQuery.data?.data ?? []).map((s) => (
                <code
                  key={s.id}
                  className="mr-1 rounded bg-surface-2 px-1 font-mono text-xs"
                >
                  {s.pve_snapshot_name}
                </code>
              ))}
            </p>
          )}
        </div>
      </Dialog>

      <Dialog
        open={confirmEchtlauf}
        onClose={() => setConfirmEchtlauf(false)}
        title="Echtlauf jetzt starten?"
        description="Der Dry-Run wird als realer Lauf ausgeführt - Systeme werden verändert."
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmEchtlauf(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              onClick={onEchtlaufConfirm}
              loading={confirmRun.isPending}
              leftIcon={<PlayCircle className="size-4" />}
              data-testid="run-confirm-echtlauf"
            >
              Echtlauf bestätigen
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2 text-sm">
          <p>
            Routine + Zielsysteme werden aus diesem Dry-Run übernommen. Vor dem Lauf
            wird automatisch ein Pre-Snapshot erzeugt (Routine-Pflicht bleibt bestehen).
          </p>
          <div>
            <span className="text-xs uppercase tracking-wider text-muted">
              Routine
            </span>
            <p>{routineLabel}</p>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-muted">
              Betroffene Systeme ({systemHostnames.length}) · Kunde {customerLabel}
            </span>
            {systemHostnames.length > 0 ? (
              <ul className="mt-1 list-disc pl-5">
                {systemHostnames.map((h, i) => (
                  <li key={`${h}-${i}`} className="font-mono text-xs">
                    {h}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted">Keine Zielsysteme.</p>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StepsList - linke Spalte, gruppiert nach system_id
// ---------------------------------------------------------------------------

function StepsList({
  steps,
  hostLabel,
}: {
  steps: Step[];
  hostLabel: (id: string | null | undefined) => string;
}): JSX.Element {
  const grouped = useMemo(() => {
    const out: Record<string, Step[]> = {};
    for (const s of steps) {
      const key = s.system_id ?? 'global';
      (out[key] ??= []).push(s);
    }
    return out;
  }, [steps]);

  if (Object.keys(grouped).length === 0) {
    return (
      <Card className="text-sm text-muted">
        Noch keine Steps. Sobald Ansible startet, erscheinen sie hier.
      </Card>
    );
  }

  return (
    <Card className="max-h-[480px] overflow-y-auto">
      <ul className="flex flex-col gap-2">
        {Object.entries(grouped).map(([key, hostSteps]) => (
          <HostStepsItem
            key={key}
            label={key === 'global' ? 'global' : hostLabel(key)}
            steps={hostSteps}
          />
        ))}
      </ul>
    </Card>
  );
}

function HostStepsItem({ label, steps }: { label: string; steps: Step[] }): JSX.Element {
  const [open, setOpen] = useState(true);
  const summary = countByStatus(steps);
  // Erste Ursache dieses Hosts - reicht als Kurzhinweis neben dem Hostnamen.
  const ursache = steps.find((s) => s.failure_reason)?.failure_reason ?? null;
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1 rounded-md px-1 py-1 text-left hover:bg-surface-2"
      >
        {open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
        <span className="font-mono text-xs">{label}</span>
        <span className="ml-auto inline-flex gap-1">
          {summary.success > 0 && <Badge tone="success">{summary.success} ok</Badge>}
          {ursache ? (
            <Badge tone={failureTone(ursache.group)}>{ursache.short_label}</Badge>
          ) : (
            summary.failed > 0 && <Badge tone="danger">{summary.failed} failed</Badge>
          )}
          {summary.running > 0 && <Badge tone="primary">{summary.running} running</Badge>}
        </span>
      </button>
      {open && (
        <ul className="mt-1 flex flex-col gap-0.5 pl-4 text-xs">
          {steps.map((s) => (
            <li key={s.id} className="flex items-center gap-1.5 truncate">
              <StepStatusDot status={s.status} reason={s.failure_reason ?? null} />
              <span className="truncate">{s.name}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

// Step-Status-Normalisierung (ADR-011 Etappe 2): Client-Runs liefern zwei
// neue Stati - 'unreachable' (Host down, rc=4) zählt als Fehler, 'changed'
// (Step erfolgreich MIT Änderungen) als Erfolg. Alle Mapping-Stellen (Dot,
// Badge, Zähler) nutzen diese Helfer, damit die Darstellung konsistent bleibt.
function normalizeStepStatus(status: string): string {
  if (status === 'unreachable') return 'failed';
  if (status === 'changed') return 'success';
  return status;
}

// Klartext-Label für die neuen Stati - sonst Backend-String durchreichen.
function stepStatusLabel(status: string): string {
  if (status === 'unreachable') return 'unerreichbar';
  if (status === 'changed') return 'geändert';
  return status;
}

function countByStatus(steps: Step[]): {
  pending: number;
  running: number;
  success: number;
  failed: number;
  skipped: number;
} {
  const out = { pending: 0, running: 0, success: 0, failed: 0, skipped: 0 };
  for (const s of steps) {
    const k = normalizeStepStatus(s.status) as keyof typeof out;
    if (k in out) out[k] += 1;
  }
  return out;
}

function StepStatusDot({
  status,
  reason,
}: {
  status: RunStep['status'];
  reason?: StepFailureReason | null;
}): JSX.Element {
  const normalized = normalizeStepStatus(status);
  // Fehlschläge ohne Zielsystem-Kontakt sind gelb, nicht rot - dann sieht man
  // sofort, dass da nichts kaputt ist, sondern nur der Zugang fehlt.
  const fehlerFarbe = reason && failureTone(reason.group) === 'warning' ? 'bg-warning' : 'bg-danger';
  const tone =
    normalized === 'failed'
      ? fehlerFarbe
      : normalized === 'running'
        ? 'bg-primary'
        : normalized === 'skipped'
          ? 'bg-muted'
          : normalized === 'success'
            ? 'bg-success'
            : 'bg-zinc-400';
  const beschriftung = reason ? reason.title : stepStatusLabel(status);
  return (
    <span
      className={`inline-block size-2 shrink-0 rounded-full ${tone}`}
      aria-label={beschriftung}
      title={beschriftung}
    />
  );
}

// ---------------------------------------------------------------------------
// StepsTable - Tab "Steps"
// ---------------------------------------------------------------------------

export function StepsTable({
  steps,
  hostLabel,
}: {
  steps: Step[];
  hostLabel: (id: string | null | undefined) => string;
}): JSX.Element {
  const columns = useMemo((): ReadonlyArray<Column<Step>> => {
    return [
      {
        id: 'seq',
        header: 'Seq',
        className: 'font-mono text-xs',
        filterValue: (s) => String(s.sequence),
        cell: (s) => s.sequence,
      },
      {
        id: 'system',
        header: 'System',
        className: 'font-mono text-xs',
        filterValue: (s) => (s.system_id ? hostLabel(s.system_id) : ''),
        cell: (s) => (s.system_id ? hostLabel(s.system_id) : '–'),
      },
      {
        id: 'step',
        header: 'Step',
        cell: (s) => s.name,
      },
      {
        id: 'status',
        header: 'Status',
        filter: 'select',
        filterValue: (s) => s.failure_reason?.short_label ?? stepStatusLabel(s.status),
        cell: (s) => {
          const reason = s.failure_reason ?? null;
          return (
            /* Bei Fehlschlägen steht hier die Kurzursache statt eines
               nichtssagenden "failed" - "Yubikey fehlt" ist die
               Information, die zählt. */
            <Badge
              tone={
                reason
                  ? failureTone(reason.group)
                  : normalizeStepStatus(s.status) === 'failed'
                    ? 'danger'
                    : normalizeStepStatus(s.status) === 'running'
                      ? 'primary'
                      : normalizeStepStatus(s.status) === 'skipped'
                        ? 'neutral'
                        : normalizeStepStatus(s.status) === 'success'
                          ? 'success'
                          : 'neutral'
              }
            >
              {reason ? reason.short_label : stepStatusLabel(s.status)}
            </Badge>
          );
        },
      },
      {
        id: 'dauer',
        header: 'Dauer',
        className: 'font-mono text-xs',
        filterValue: (s) => (s.duration_seconds != null ? `${s.duration_seconds}s` : ''),
        cell: (s) => (s.duration_seconds != null ? `${s.duration_seconds}s` : '–'),
      },
      {
        id: 'started',
        header: 'Started',
        className: 'text-xs',
        filterValue: (s) => s.started_at ?? '',
        cell: (s) => <RelativeTime value={s.started_at ?? null} />,
      },
    ];
  }, [hostLabel]);

  if (steps.length === 0) {
    return <p className="text-sm text-muted">Keine Steps vorhanden.</p>;
  }
  return (
    <Card>
      <DataTable<Step>
        caption="Steps dieses Laufs"
        columns={columns}
        rows={steps}
        rowKey={(s) => s.id}
        storageKey="run-steps"
        defaultPageSize={100}
        rowExtra={(s) => {
          const reason = s.failure_reason ?? null;
          return reason ? <StepFailureCallout step={s} reason={reason} /> : null;
        }}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Klartext-Ursachen (Vorfall 11.08.2026)
//
// Vorher: neun rote Zeilen "fehlgeschlagen", Ursache nur in der Ansible-
// Rohausgabe. Jetzt steht die Ursache oben und die Rohausgabe darunter zum
// Aufklappen - nicht umgekehrt.
// ---------------------------------------------------------------------------

/** Icon je Kategorie/Gruppe - der Yubikey-Fall bekommt sein eigenes. */
function FailureIcon({
  category,
  group,
  className,
}: {
  category: string;
  group: string;
  className: string;
}): JSX.Element {
  if (category === 'ssh_key_not_confirmed') {
    return <Fingerprint className={className} aria-hidden="true" />;
  }
  switch (group) {
    case 'verbindung':
      return <Unplug className={className} aria-hidden="true" />;
    case 'konfiguration':
      return <Settings2 className={className} aria-hidden="true" />;
    case 'zielsystem':
      return <ServerCog className={className} aria-hidden="true" />;
    case 'wartung':
      return <XCircle className={className} aria-hidden="true" />;
    default:
      return <CircleHelp className={className} aria-hidden="true" />;
  }
}

/** Prominenter Kasten oben im Lauf-Detail: warum ist dieser Lauf rot? */
export function RunFailureBanner({ failure }: { failure: RunFailureSummary }): JSX.Element {
  return (
    <section
      aria-label="Ursache des Fehlschlags"
      data-testid="run-failure-banner"
      className={`flex flex-wrap items-start gap-3 rounded-md border p-3 ${failureBoxClasses(
        failure.group
      )}`}
    >
      <FailureIcon
        category={failure.category}
        group={failure.group}
        className={`mt-0.5 size-5 shrink-0 ${failureTextClass(failure.group)}`}
      />
      <div className="min-w-0 flex-1">
        <p className={`text-base font-semibold ${failureTextClass(failure.group)}`}>
          {failure.title}
        </p>
        <p className="mt-1 text-sm">{failure.action}</p>
        <p className="mt-2 text-xs text-muted">
          {failureGroupLabel(failure.group)}
          {failure.steps_failed > 1 && (
            <>
              {' · '}
              {failure.steps_affected} von {failure.steps_failed} fehlgeschlagenen Schritten
            </>
          )}
          {failure.retryable && ' · Lauf ist unverändert wiederholbar'}
        </p>
      </div>
      <Badge tone={failureTone(failure.group)}>{failure.short_label}</Badge>
    </section>
  );
}

/**
 * Rückfallkasten für 'blocked' ohne klassifizierte Ursache.
 *
 * Vorfall 13.08.2026: Ein blockierter Lauf ist KEIN Fehlschlag - er hat nie
 * stattgefunden. Steht keine Step-Ursache zur Verfügung (Lauf ohne
 * auswertbare Ausgabe), sagt wenigstens dieser Kasten, was Sache ist.
 */
export function BlockedRunBanner(): JSX.Element {
  return (
    <section
      aria-label="Lauf blockiert"
      data-testid="run-blocked-banner"
      className="flex flex-wrap items-start gap-3 rounded-md border border-warning/30 bg-warning/5 p-3"
    >
      <Unplug className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold text-warning">
          Kein Zugang – der Lauf hat nie stattgefunden.
        </p>
        <p className="mt-1 text-sm">
          Der Client kam auf kein Zielsystem drauf. Am Kundensystem wurde nichts
          verändert; sobald der Zugang steht, ist der Lauf unverändert wiederholbar.
        </p>
        <p className="mt-2 text-xs text-muted">
          Zugang prüfen: Schlüssel hinterlegt (ssh-copy-id)? Yubikey gesteckt und
          berührt? Bitwarden-Sitzung offen (bw unlock)? VPN aktiv?
        </p>
      </div>
      <Badge tone="warning">nichts ausgeführt</Badge>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Snapshot-Verzicht am Lauf (Befund 20.08.2026, Migration 0029)
//
// Bei L-Quadrat war der Pre-Snapshot fachlich falsch (Ceph-Pool 84,83 % bei
// nearfull 85 %) und der Lauf wurde per SQL am Gate vorbei eingekippt - ohne
// Grund, ohne Beleg, ohne Spur. Seit dem begründeten Verzicht trägt der
// Lauf alle vier Angaben, und dieser Kasten zeigt sie. Gelb, nicht rot: der
// Verzicht war eine bewusste, begründete Entscheidung, kein Schaden.
// ---------------------------------------------------------------------------

/** Sichtbare Spur des begründeten Snapshot-Verzichts. */
export function SnapshotWaiverNotice({
  reason,
  evidence,
  at,
  actor,
}: {
  reason: string;
  evidence?: string | null;
  at?: string | null;
  actor?: string | null;
}): JSX.Element {
  return (
    <section
      aria-label="Snapshot-Verzicht"
      data-testid="run-waiver-notice"
      className="flex flex-wrap items-start gap-3 rounded-md border border-warning/40 bg-warning/5 p-3"
    >
      <CameraOff className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-warning">
          Dieser Lauf fuhr ohne Pre-Snapshot &mdash; bewusster, begründeter Verzicht.
        </p>
        <p className="mt-1 text-sm" data-testid="run-waiver-reason">
          {reason}
        </p>
        {evidence && (
          <p className="mt-1 text-sm text-muted" data-testid="run-waiver-evidence">
            Ersatzsicherung: {evidence}
          </p>
        )}
        <p className="mt-2 text-xs text-muted" data-testid="run-waiver-meta">
          Erklärt
          {actor && (
            <>
              {' '}
              von <code className="font-mono">{actor}</code>
            </>
          )}
          {at && (
            <>
              {' '}
              <RelativeTime value={at} variant="absolute-with-tooltip" />
            </>
          )}
          . Ohne Snapshot gibt es keinen 1-Klick-Rollback &mdash; im Fehlerfall
          zählt die benannte Ersatzsicherung.
        </p>
      </div>
      <Badge tone="warning">ohne Snapshot</Badge>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Client-Fassung am Lauf (Vorfall 13.08.2026)
//
// Colins wt-client lief seit dem Vortag durch und hielt damit den Code vom
// 12.08. im Speicher. Der Client MERKT das inzwischen selbst - nur schreibt er
// die Warnung nach stderr in einen abgetrennten screen, den niemand liest.
// Colin sitzt im Browser, also gehört der Befund hierher, an den Lauf.
//
// Drei Zustände, drei Optiken - und der dritte ist der wichtige:
//   true   ein Hinweis, kein Alarm. Der Vertrag von client_outdated ist
//          RICHTUNGSLOS ("weicht von der ausgelieferten Fassung ab"), also
//          darf hier nicht "veralteter Client" behauptet werden: läuft der
//          Client aus dem Repo-Arbeitsverzeichnis und ist noch nicht deployt,
//          ist der Server die ältere Seite - auf diesem Mac der Dauerzustand
//          (wt-client.py nennt ihn ausdrücklich den Normalfall). Ein gelber
//          Alarm auf jedem Lauf plus der Rat "neu starten", von dem der Client
//          selbst weiß, dass er nichts ändert, macht den Kasten binnen zwei
//          Wartungsabenden zu Tapete - und dann greift der echte Fall nicht
//          mehr. Also: beide Richtungen nennen, Rat je Richtung, und den
//          Einzeiler dazu, mit dem sich die Richtung in einer Sekunde klären
//          lässt (gemeldeter Hash gegen die lokale Datei).
//   false  eine ruhige Zeile. Geprüft, passt.
//   null   eine ruhige Zeile, aber ausdrücklich als UNGEPRÜFT beschriftet.
//          Ein grüner Haken auf einer Annahme wäre derselbe Fehler wie am
//          13.08., nur mit besserem Gewissen.
// ---------------------------------------------------------------------------

/** Fassung des wt-clients, der diesen Lauf abgeholt und ausgeführt hat. */
export function ClientVersionNotice({
  version,
  outdated,
}: {
  version?: string | null;
  outdated?: boolean | null;
}): JSX.Element {
  // Der volle SHA256 sagt niemandem etwas; die ersten Stellen reichen, um zwei
  // Läufe zu vergleichen ("lief der von gestern mit derselben Fassung?").
  const kurz = version ? version.slice(0, 12) : null;

  if (outdated === true) {
    return (
      <section
        aria-label="Abweichende Client-Fassung"
        data-testid="run-client-outdated"
        className="flex flex-wrap items-start gap-3 rounded-md border border-border bg-surface-2 p-3"
      >
        <History className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">
            Client-Fassung weicht von der ausgelieferten ab.
          </p>
          <p className="mt-1 text-sm text-muted">
            Der wt-client hat beim Abholen einen anderen SHA256 gemeldet, als der
            Server unter <code className="font-mono">/client/download</code> bereitstellt.
            Der Vergleich sagt nur, <strong>dass</strong> die beiden abweichen — nicht,
            welche Seite die ältere ist. Was in diesem Lauf lief, steht fest: die
            gemeldete Fassung. Offen ist nur, ob sie die neuere oder die ältere der
            beiden ist. Zwei Möglichkeiten:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-muted">
            <li data-testid="run-client-outdated-fall-client">
              <strong>Der Client ist älter:</strong> Der Prozess läuft noch mit dem Code
              von vor dem letzten Deploy — Python liest die Datei nur beim Start. Dann
              hilft ein Neustart: <code className="font-mono">screen -r wt-client</code>,
              Strg+C, wieder starten, Lauf wiederholen.
            </li>
            <li data-testid="run-client-outdated-fall-server">
              <strong>Der Server ist älter:</strong> Die wt-client.py am Admin-Mac ist
              neuer als die deployte. Ein Neustart ändert daran <strong>nichts</strong> —
              er lädt wieder dieselbe lokale Datei; fällig wäre ein Deploy. Wird gerade
              am Client gearbeitet, ist das der Normalfall und kein Fehler.
            </li>
          </ul>
          {/* Der Einzeiler, der die Richtung klärt: gemeldet wird der Hash der
              Datei, mit der der laufende Prozess gestartet ist. Stimmt er mit
              der Datei auf der Platte überein, ist der Client aktuell und die
              Abweichung sitzt auf der Serverseite. */}
          {kurz ? (
            <p className="mt-2 text-xs text-muted" data-testid="run-client-outdated-check">
              Gemeldet: <code className="break-all font-mono">{kurz}…</code> — welcher
              Fall gilt, klärt{' '}
              <code className="font-mono">shasum -a 256 client/wt-client.py</code> am
              Admin-Mac: Beginnt die Ausgabe mit <code className="font-mono">{kurz}</code>,
              lief der Client mit der aktuellen lokalen Datei — also der zweite Fall
              (Deploy offen). Weicht sie ab, ist der Prozess älter als seine eigene
              Datei — erster Fall (Neustart).
            </p>
          ) : null}
        </div>
        <Badge tone="neutral">Fassung weicht ab</Badge>
      </section>
    );
  }

  if (outdated === false) {
    return (
      <p
        data-testid="run-client-current"
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted"
      >
        <CheckCircle2 className="size-3.5 shrink-0 text-success" aria-hidden="true" />
        <span>
          Client aktuell — die ausführende Fassung stimmt mit der ausgelieferten überein.
        </span>
        {kurz && <code className="font-mono">{kurz}…</code>}
      </p>
    );
  }

  return (
    <p
      data-testid="run-client-unknown"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted"
    >
      <CircleHelp className="size-3.5 shrink-0" aria-hidden="true" />
      <span>
        Client-Fassung ungeprüft — <strong>nicht</strong> dasselbe wie „aktuell“.{' '}
        {version
          ? 'Der Client hat seine Fassung gemeldet, aber der Server kann die ausgelieferte gerade nicht bestimmen.'
          : 'Der Client hat beim Abholen keine Fassung gemeldet (älterer Client oder kein Client-Lauf).'}
      </span>
      {kurz && <code className="font-mono">{kurz}…</code>}
    </p>
  );
}

/** Ursache eines einzelnen Steps, Rohausgabe darunter aufklappbar. */
export function StepFailureCallout({
  step,
  reason,
}: {
  step: Step;
  reason: StepFailureReason;
}): JSX.Element {
  const roh = rohausgabe(step);
  return (
    <div
      className={`rounded-md border p-3 ${failureBoxClasses(reason.group)}`}
      data-testid="step-failure-callout"
    >
      <div className="flex items-start gap-2">
        <FailureIcon
          category={reason.category}
          group={reason.group}
          className={`mt-0.5 size-4 shrink-0 ${failureTextClass(reason.group)}`}
        />
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-semibold ${failureTextClass(reason.group)}`}>
            {reason.title}
          </p>
          <p className="mt-1 text-sm">{reason.action}</p>
          {reason.retryable && (
            <p className="mt-1 text-xs text-muted">
              Am Zielsystem wurde nichts verändert - der Lauf kann unverändert
              wiederholt werden.
            </p>
          )}
          <p className="mt-2 break-words font-mono text-xs text-muted">
            {reason.evidence
              ? `Meldung: ${reason.evidence}`
              : 'Keine eindeutige Meldung in der Ausgabe.'}
          </p>
          {roh ? (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-muted hover:text-text">
                Rohausgabe anzeigen
              </summary>
              <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-surface-2 p-2 font-mono text-[11px]">
                {roh}
              </pre>
            </details>
          ) : (
            <p className="mt-2 text-xs text-muted">Keine Ausgabe gespeichert.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SnapshotsPane - Tab "Snapshots"
// ---------------------------------------------------------------------------

interface SnapshotsPaneProps {
  snapshots: ReturnType<typeof useRunSnapshots>['data'] extends infer T
    ? T extends { data: infer D }
      ? D
      : never
    : never;
  isLoading: boolean;
  error: Error | null;
}

const SNAPSHOT_COLUMNS: ReadonlyArray<Column<Snapshot>> = [
  {
    id: 'snapshot',
    header: 'Snapshot',
    className: 'font-mono text-xs',
    cell: (s) => s.pve_snapshot_name,
  },
  {
    id: 'pve-node',
    header: 'PVE-Node',
    className: 'font-mono text-xs',
    cell: (s) => s.pve_node,
  },
  {
    id: 'vmid',
    header: 'VMID',
    className: 'font-mono text-xs',
    filterValue: (s) => String(s.pve_vmid),
    cell: (s) => s.pve_vmid,
  },
  {
    id: 'state',
    header: 'State',
    filter: 'select',
    filterValue: (s) => s.state,
    cell: (s) => (
      <Badge
        tone={
          s.state === 'created' || s.state === 'restored'
            ? 'success'
            : s.state === 'failed'
              ? 'danger'
              : 'neutral'
        }
      >
        {s.state}
      </Badge>
    ),
  },
  {
    id: 'created',
    header: 'Created',
    className: 'text-xs',
    filterValue: (s) => s.created_at_pve ?? s.requested_at,
    cell: (s) => <RelativeTime value={s.created_at_pve ?? s.requested_at} />,
  },
];

function SnapshotsPane({ snapshots, isLoading, error }: SnapshotsPaneProps): JSX.Element {
  if (isLoading) return <SkeletonRows rows={2} cols={4} />;
  if (error) return <ErrorAlert error={error} />;
  if (!snapshots || snapshots.length === 0) {
    return <p className="text-sm text-muted">Keine Snapshots für diesen Run.</p>;
  }
  return (
    <Card>
      <DataTable
        caption="Snapshots dieses Laufs"
        columns={SNAPSHOT_COLUMNS}
        rows={snapshots}
        rowKey={(s) => s.id}
        storageKey="run-snapshots"
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// DiffTabContent - Tab "Diff" (Sprint 6 Mount, WT-504)
// ---------------------------------------------------------------------------

import type { RunDiffResponse } from '@/hooks/useRuns';

function DiffTabContent({
  diff,
  isLoading,
  error,
  isDryRun,
}: {
  diff: RunDiffResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  isDryRun: boolean;
}): JSX.Element {
  if (isLoading) return <SkeletonRows rows={3} cols={3} />;
  if (error) return <ErrorAlert error={error} />;
  if (!diff) return <p className="text-sm text-muted">Kein Diff geladen.</p>;
  return (
    <div className="flex flex-col gap-3">
      {!isDryRun && (
        <p className="rounded-md border border-border bg-surface-2/40 p-2 text-xs text-muted">
          Echtlauf: Diff ist die Aufzeichnung der durchgeführten Änderungen
          (nicht ein hypothetischer Plan).
        </p>
      )}
      <DiffPane diff={diff} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// RealRunCta - prominenter "Echtlauf starten"-Banner nach erfolgreichem Dry-Run
// ---------------------------------------------------------------------------

function RealRunCta({
  diffSystemCount,
  hasDiff,
  onShowDiff,
  onStartEchtlauf,
  starting,
}: {
  diffSystemCount: number;
  /** false bei Client-Runs ohne structured Diff (ADR-011 Etappe 2). */
  hasDiff: boolean;
  onShowDiff: () => void;
  onStartEchtlauf: () => void;
  starting: boolean;
}): JSX.Element {
  return (
    <section
      aria-label="Echtlauf-CTA"
      className="flex flex-wrap items-center gap-3 rounded-md border border-primary/30 bg-primary/5 p-3"
    >
      <Diff className="size-5 text-primary" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {hasDiff ? (
          <>
            <p className="text-sm font-medium text-primary">
              Dry-Run sauber - {diffSystemCount} System(e) haetten Aenderungen.
            </p>
            <p className="text-xs text-muted">
              Wenn das Diff korrekt aussieht, kannst du diesen Dry-Run direkt als
              Echtlauf bestätigen - Routine + Systeme werden übernommen, ein
              Pre-Snapshot wird automatisch erzeugt.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-primary">
              Dry-Run abgeschlossen - kein Änderungs-Diff verfügbar
              (Client-Ausführung).
            </p>
            <p className="text-xs text-muted">
              Details stehen nur in den Logs. Prüfe die Log-Ausgabe und
              bestätige den Echtlauf auf eigene Verantwortung - Routine +
              Systeme werden übernommen, ein Pre-Snapshot wird automatisch
              erzeugt.
            </p>
          </>
        )}
      </div>
      <Button variant="ghost" size="sm" onClick={onShowDiff}>
        {hasDiff ? 'Diff ansehen' : 'Logs ansehen'}
      </Button>
      <Button
        leftIcon={<PlayCircle className="size-4" />}
        size="sm"
        onClick={onStartEchtlauf}
        loading={starting}
        data-testid="run-start-echtlauf"
      >
        Echtlauf starten
      </Button>
    </section>
  );
}
